import React, { memo, useState, useEffect, ChangeEvent } from 'react'
import { useDispatch } from 'react-redux'
import { Select, ISelectOption } from 'components/forms/Selects'
import { Autocomplete } from 'components/forms/autocomplete/DPLAutocomplete'
import { TextInput } from 'components/atoms'
import { FormModal } from 'transfix-ui'
import { ISpotBiddingConfig, CONFIG_TYPE, RATE_TYPE } from '../types/spotBiddingConfig'
import {
  createSpotBiddingConfig,
  updateSpotBiddingConfig,
} from '../utils/actions/spotBiddingConfig'
import { FuelDetails } from './form-components/FuelDetails'
import { ShipperMultiSelect } from './form-components/ShipperMultiSelect'

interface ISpotBiddingConfigModalProps {
  showModal: boolean
  closeModalHandler: () => void
  configToEdit?: ISpotBiddingConfig
  error?: string
}

const defaultState: Partial<ISpotBiddingConfig> = {
  configType: CONFIG_TYPE.shipper,
  shipperApplications: [],
  rateType: RATE_TYPE.allIn,
}

export const CONFIG_TYPE_OPTIONS = [
  {
    label: 'Single',
    value: CONFIG_TYPE.shipper,
  },
  {
    label: 'Shipper Segment',
    value: CONFIG_TYPE.bucket,
  },
]

export const SpotBiddingConfigModal = memo<ISpotBiddingConfigModalProps>(
  ({ closeModalHandler, configToEdit, error, showModal }) => {
    const dispatch = useDispatch()
    const [config, setConfig] = useState<Partial<ISpotBiddingConfig>>(defaultState)
    const { configName, configType, shipperApplications = [], rateType, fuelRpm } = config

    useEffect(() => {
      if (showModal && configToEdit) {
        setConfig({
          id: configToEdit.id,
          configName: configToEdit.configName,
          configType: configToEdit.configType,
          shipperApplications: configToEdit.shipperApplications,
          rateType: configToEdit.rateType,
          fuelRpm: configToEdit.fuelRpm,
          modifyPastPickupType: configToEdit.modifyPastPickupType,
        })
      } else if (!showModal) {
        setConfig(defaultState)
      }
    }, [showModal, configToEdit])

    const formStateChangeHandler = (change: Partial<ISpotBiddingConfig>) =>
      setConfig(current => ({ ...current, ...change }))

    const configTypeSelectionHandler = (selection: ISelectOption<CONFIG_TYPE>): void => {
      if (selection.value === CONFIG_TYPE.shipper)
        formStateChangeHandler({ shipperApplications: [] })
      formStateChangeHandler({ configType: selection.value })
    }

    const configNameChangeHandler = (e: ChangeEvent<HTMLInputElement>) =>
      formStateChangeHandler({ configName: e.target.value })

    const updateShipperHandler = ({
      fullSelection,
      value: id,
    }: {
      value: string
      fullSelection?: { name: string }
    }) => {
      formStateChangeHandler({
        shipperApplications: [
          {
            name: fullSelection?.name || '',
            id,
          },
        ],
      })
    }

    const submitForm = () => {
      if (configToEdit) {
        dispatch(updateSpotBiddingConfig(config))
      } else {
        dispatch(createSpotBiddingConfig(config))
      }
    }

    return (
      <FormModal
        title={`${configToEdit ? 'Edit' : 'Add'} Segment`}
        size={6}
        open={showModal}
        onClose={closeModalHandler}
        error={error}
        actions={[
          {
            label: 'Cancel',
            type: 'dismiss',
            dataTest: 'cancel',
          },
          {
            label: `${configToEdit ? 'Save Changes' : 'Add'}`,
            type: 'confirm',
            action: submitForm,
            dataTest: 'save-config',
          },
        ]}>
        {showModal && (
          <>
            <p>
              <b>Select an option to start</b>
            </p>
            <div className='row'>
              <div className='col-md-4'>
                <Select
                  label='Type of Control Panel'
                  options={CONFIG_TYPE_OPTIONS}
                  value={configType}
                  large
                  onChange={configTypeSelectionHandler}
                  dataTest='modal-config-type'
                />
              </div>
              <div className='col-md-8'>
                {configType === CONFIG_TYPE.shipper && (
                  <Autocomplete
                    label='Shipper'
                    placeholder='Enter shipper name'
                    value={shipperApplications[0]?.name || ''}
                    onChange={updateShipperHandler}
                    type='shipper'
                    dataTest='modal-shipper-name'
                    large
                  />
                )}
                {configType === CONFIG_TYPE.bucket && (
                  <TextInput
                    label='Segment Name'
                    value={configName}
                    onChange={configNameChangeHandler}
                    large
                    dataTest='modal-segment-name'
                  />
                )}
              </div>
            </div>
            {configType === CONFIG_TYPE.bucket && (
              <ShipperMultiSelect
                shipperApplications={shipperApplications}
                onChange={formStateChangeHandler}
              />
            )}
            <p>
              <b>Fuel Details</b>
            </p>
            <div className='row'>
              <FuelDetails
                rateType={rateType}
                fuelRpm={fuelRpm}
                onChange={formStateChangeHandler}
              />
            </div>
          </>
        )}
      </FormModal>
    )
  }
)
